import React, { useState, useEffect, useCallback } from "react";
import { CSpinner, CTooltip } from "@coreui/react-pro";
import { useNavigate, useParams } from "react-router-dom";
import { getOrder, uploadForeignFile, uploadForeignQuote, updateOrder, uploadPropertyQuote, uploadGroupQuote, uploadOffshoreQuote, uploadTermLifeQuote, uploadPropertyFile, uploadGroupFile, uploadOffshoreFile, uploadTermLifeFile } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './uploadQuotation.css';

const UploadQuotation = () => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState({});
  const { insId, orderId, id } = useParams();
  const [initialValues, setInitialValues] = useState({
    insurance_provider: "",
    plan_name: "",
    description: "",
    quotation_info: [],
    product_disclosure_sheet: "",
    price: "",
    supporting_docs: [],
    plan_benefits: "",
    total_coverage: "",
  });
  const [productDisclosureType, setProductDisclosureType] = useState("pdf");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [insuranceProviderType, setInsuranceProviderType] = useState("select");

  const insuranceProviders = [
    "AIA Berhad",
    "Hong Leong Assurance Berhad",
    "AIA Public Takaful Berhad",
    "Allianz Life Insurance Malaysia Berhad",
    "Manulife Insurance Berhad",
    "Etiqa Family Takaful Berhad",
    "AmMetLife Insurance Berhad",
    "MCIS Insurance Berhad",
    "Hong Leong MSIG Takaful Berhad",
    "Etiqa Life Insurance Berhad",
    "Prudential Assurance Malaysia Berhad",
    "FWD Insurance Berhad",
    "Tokio Marine Life Insurance Malaysia Berhad",
    "Generali Insurance Berhad",
    "Tune Protect Ventures Sdn Bhd (Tune Protect Life)",
    "Great Eastern Life Assurance (Malaysia) Berhad",
    "Zurich Life Insurance Malaysia Berhad",
    "AIA General Berhad",
    "Lonpac Insurance Berhad",
    "AIG Malaysia Insurance Berhad",
    "MSIG Insurance (Malaysia) Berhad",
    "Allianz General Insurance Company (Malaysia) Berhad",
    "Pacific and Orient Insurance Company Berhad",
    "Berjaya Sompo Insurance Berhad",
    "QBE Insurance (Malaysia) Berhad",
    "Chubb Insurance Malaysia Berhad",
    "RHB Insurance Berhad",
    "Etiqa General Insurance Berhad",
    "The Pacific Insurance Berhad",
    "Generali Insurance Berhad",
    "Tokio Marine Insurance (Malaysia) Berhad",
    "Great Eastern General Insurance (Malaysia) Berhad",
    "Tune Insurance Malaysia Berhad",
    "Liberty General Insurance Berhad",
    "Zurich General Insurance Malaysia Berhad",
    "Etiqa General Takaful Berhad",
    "Takaful Ikhlas General Berhad",
    "Investor Trust Assurance",
    "Syarikat Takaful Malaysia Keluarga Berhad",
    "Zurich General Takaful Malaysia Berhad"
  ];

  const fetchData = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const response = await getOrder({ id });
      if (response.data) {
        setOrder(response.data);
        setInitialValues({
          insurance_provider: "",
          plan_name: "",
          description: "",
          quotation_info: "",
          product_disclosure_sheet: "",
          price: "",
          supporting_docs: [],
          plan_benefits: "",
          total_coverage: "",
        });
      } else {
        setOrder({});
        toast.error("No data found for the given ID");
      }
    } catch (error) {
      toast.error("Error fetching data");
      setOrder({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  const validationSchema = Yup.object().shape({
    insurance_provider: Yup.string().required("Insurance provider is required"),
    plan_name: Yup.string().required("Plan name is required"),
    description: Yup.string().required("Description is required"),
    quotation_info: Yup.array().of(Yup.mixed()).min(0),
    product_disclosure_sheet: Yup.mixed().test(
      "product-disclosure-sheet",
      "Product disclosure sheet is required",
      function (value) {
        if (productDisclosureType === "website") {
          return true;
        } else if (productDisclosureType === "pdf") {
          return true;
        }
        return true;
      }
    ),
    price: Yup.number().required("Price is required").positive("Price must be positive"),
    supporting_docs: Yup.array().of(Yup.mixed().test(
      "fileSize",
      "File size is too large",
      value => !value || (value && value.size <= 2000000) // 2MB
    )).max(2, "You can upload a maximum of 2 files"),
    plan_benefits: Yup.string().required("Benefit is required").test(
      "plan-benefits",
      "Benefit is required",
      function (value) {
        return value && value.trim() !== "";
      }
    ),
    total_coverage: Yup.number().positive("Total coverage must be positive"),
  });

  const handleProductDisclosureTypeChange = (event, setFieldValue) => {
    setProductDisclosureType(event.target.value);
    setFieldValue("product_disclosure_sheet", productDisclosureType === "pdf" ? [] : "");
  };

  const handleInsuranceProviderTypeChange = (event, setFieldValue) => {
    setInsuranceProviderType(event.target.value);
    setFieldValue("insurance_provider", event.target.value === "other" ? "" : event.target.value);
  };

  const handleEditorChange = (state, setFieldValue, setFieldTouched) => {
    setEditorState(state);
    const content = state.getCurrentContent();
    const isEmpty = !content.hasText() && content.getBlockMap().first().getType() === 'unstyled';
    const plainText = content.getPlainText().trim();
    setFieldValue("plan_benefits", isEmpty || plainText === "" ? "" : draftToHtml(convertToRaw(content)));
    setFieldTouched("plan_benefits", true, false);
  };

  const uploadQuotation = async (formData) => {
    switch (parseInt(insId, 10)) {
      case 1:
        return uploadForeignQuote(formData);
      case 8:
        return uploadPropertyQuote(formData);
      case 7:
        return uploadGroupQuote(formData);
      case 6:
        return uploadOffshoreQuote(formData);
      case 2:
        return uploadTermLifeQuote(formData);
      default:
        throw new Error("Invalid insurance type");
    }
  };

  const uploadFile = async (formData) => {
    switch (parseInt(insId, 10)) {
      case 1:
        return uploadForeignFile(formData);
      case 8:
        return uploadPropertyFile(formData);
      case 7:
        return uploadGroupFile(formData);
      case 6:
        return uploadOffshoreFile(formData);
      case 2:
        return uploadTermLifeFile(formData);
      default:
        throw new Error("Invalid insurance type");
    }
  };

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      if (!order || Object.keys(order).length === 0) {
        throw new Error("Order data is not available");
      }

      if (values.supporting_docs.length > 2) {
        throw new Error("You can upload a maximum of 2 files");
      }

      if (productDisclosureType === "website" && typeof values.product_disclosure_sheet === "string") {
        values.product_disclosure_sheet = values.product_disclosure_sheet;
      } else if (productDisclosureType === "pdf" && !Array.isArray(values.product_disclosure_sheet)) {
        values.product_disclosure_sheet = "";
      }

      const formDataSupportingDocs = new FormData();
      const formDataQuotationInfo = new FormData();
      const formDataProductDisclosureSheet = new FormData();

      if (Array.isArray(values.supporting_docs)) {
        values.supporting_docs.forEach((file) => {
          if (file) {
            formDataSupportingDocs.append("file", file);
          }
        });
      }
      if (values.quotation_info && values.quotation_info.length > 0) {
        formDataQuotationInfo.append("file", values.quotation_info[0]);
      }
      if (values.product_disclosure_sheet && productDisclosureType === "pdf" && values.product_disclosure_sheet.length > 0) {
        formDataProductDisclosureSheet.append("file", values.product_disclosure_sheet[0]);
      }

      let uploadResponseSupportingDocs = [];
      let uploadResponseQuotationInfo, uploadResponseProductDisclosureSheet;
      if (formDataSupportingDocs.has("file")) {
        for (let file of formDataSupportingDocs.getAll("file")) {
          const formData = new FormData();
          formData.append("file", file);
          const response = await uploadFile(formData);
          if (!response.data || !response.data[0].file) {
            throw new Error("Failed to upload supporting documents");
          }
          uploadResponseSupportingDocs.push(response.data[0].file);
        }
        toast.success("Other Files uploaded successfully!");
      }

      if (formDataQuotationInfo.has("file")) {
        uploadResponseQuotationInfo = await uploadFile(formDataQuotationInfo);
        if (!uploadResponseQuotationInfo.data || !uploadResponseQuotationInfo.data[0].file) {
          throw new Error("Failed to upload quotation info");
        }
        toast.success("Quotation info uploaded successfully!");
      }
      if (formDataProductDisclosureSheet.has("file")) {
        uploadResponseProductDisclosureSheet = await uploadFile(formDataProductDisclosureSheet);
        if (!uploadResponseProductDisclosureSheet.data || !uploadResponseProductDisclosureSheet.data[0].file) {
          throw new Error("Failed to upload product disclosure sheet");
        }
        toast.success("Product disclosure sheet uploaded successfully!");
      }

      const supportingDocsUrls = uploadResponseSupportingDocs;
      const quotationInfoUrls = uploadResponseQuotationInfo ? uploadResponseQuotationInfo.data[0].file : "";
      const productDisclosureSheetUrls = uploadResponseProductDisclosureSheet ? uploadResponseProductDisclosureSheet.data[0].file : values.product_disclosure_sheet;

      const payload = {
        enquiry_id: order.id,
        quotations: [
          {
            insurance_provider: values.insurance_provider,
            plan_name: values.plan_name,
            description: values.description,
            quotation_info: quotationInfoUrls,
            product_disclosure_sheet: productDisclosureSheetUrls,
            price: values.price,
            other_files: supportingDocsUrls,
            plan_benefits: values.plan_benefits,
            total_coverage: values.total_coverage ? values.total_coverage.toString() : undefined, // Convert total_coverage to string if it exists
          },
        ],
      };

      await uploadQuotation(payload);

      try {
        const response = await updateOrder({ id: orderId, order_status: "Quotation is Ready" });
        if (response.success) {
          toast.success("Order status updated successfully");
        } else {
          throw new Error(response.message || "Failed to update status");
        }
      } catch (error) {
        toast.error("Error updating status");
      }

      toast.success("Quotation created successfully!");
      navigate("/orders");
    } catch (error) {
      toast.error(
        error.message || "Failed to create quotation. Please try again."
      );
      setErrors({ submit: "Failed to create quotation", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, setFieldValue, setFieldTouched, values }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div className="card border-light">
                <div className="card-body">
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="insurance_provider" className="form-label">
                        <span className="text-danger">*</span> Insurance Provider
                      </label>
                      <select
                        id="insurance_provider"
                        name="insurance_provider"
                        className={`form-control ${touched.insurance_provider && errors.insurance_provider ? "is-invalid" : ""}`}
                        value={insuranceProviderType}
                        onChange={(event) => handleInsuranceProviderTypeChange(event, setFieldValue)}
                      >
                        <option value="select">Select Insurance Provider</option>
                        {insuranceProviders.map((provider, index) => (
                          <option key={index} value={provider}>
                            {provider}
                          </option>
                        ))}
                        <option value="other">Other</option>
                      </select>
                      {insuranceProviderType === "other" && (
                        <Field
                          name="insurance_provider"
                          as="input"
                          type="text"
                          className={`form-control mt-2 ${touched.insurance_provider && errors.insurance_provider ? "is-invalid" : ""}`}
                          placeholder="Enter Insurance Provider"
                        />
                      )}
                      <ErrorMessage
                        name="insurance_provider"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="plan_name" className="form-label">
                        <span className="text-danger">*</span> Plan Name
                      </label>
                      <Field
                        name="plan_name"
                        as="input"
                        type="text"
                        className={`form-control ${touched.plan_name && errors.plan_name ? "is-invalid" : ""}`}
                      />
                      <ErrorMessage
                        name="plan_name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="description" className="form-label">
                        <span className="text-danger">*</span> Description
                      </label>
                      <Field
                        name="description"
                        as="input"
                        type="text"
                        className={`form-control ${touched.description && errors.description ? "is-invalid" : ""}`}
                      />
                      <ErrorMessage
                        name="description"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="quotation_info" className="form-label">
                        Quotation Info
                      </label>
                      <input
                        id="quotation_info"
                        name="quotation_info"
                        type="file"
                        className={`form-control ${touched.quotation_info && errors.quotation_info ? "is-invalid" : ""}`}
                        onChange={(event) => {
                          setFieldValue("quotation_info", Array.from(event.currentTarget.files).slice(0, 1));
                        }}
                      />
                      <ErrorMessage
                        name="quotation_info"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="product_disclosure_type" className="form-label">
                        Product Disclosure Sheet Type
                      </label>
                      <select
                        id="product_disclosure_type"
                        name="product_disclosure_type"
                        className="form-control"
                        value={productDisclosureType}
                        onChange={(event) => handleProductDisclosureTypeChange(event, setFieldValue)}
                      >
                        <option value="pdf">PDF</option>
                        <option value="website">Website</option>
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="product_disclosure_sheet" className="form-label">
                        Product Disclosure Sheet
                      </label>
                      {productDisclosureType === "pdf" ? (
                        <input
                          id="product_disclosure_sheet"
                          name="product_disclosure_sheet"
                          type="file"
                          className={`form-control ${touched.product_disclosure_sheet && errors.product_disclosure_sheet ? "is-invalid" : ""}`}
                          onChange={(event) => {
                            setFieldValue("product_disclosure_sheet", Array.from(event.currentTarget.files).slice(0, 1));
                          }}
                        />
                      ) : (
                        <Field
                          name="product_disclosure_sheet"
                          as="input"
                          type="text"
                          className={`form-control ${touched.product_disclosure_sheet && errors.product_disclosure_sheet ? "is-invalid" : ""}`}
                        />
                      )}
                      <ErrorMessage
                        name="product_disclosure_sheet"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="price" className="form-label">
                        <span className="text-danger">*</span> Price
                      </label>
                      <Field
                        name="price"
                        as="input"
                        type="number"
                        className={`form-control ${touched.price && errors.price ? "is-invalid" : ""}`}
                      />
                      <ErrorMessage
                        name="price"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="total_coverage" className="form-label">
                        Total Coverage
                      </label>
                      <Field
                        name="total_coverage"
                        as="input"
                        type="number"
                        className={`form-control ${touched.total_coverage && errors.total_coverage ? "is-invalid" : ""}`}
                      />
                      <ErrorMessage
                        name="total_coverage"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    
                  </div>
                  <div className="row">
                  <div className="col-md-6 mb-3">
                      <label htmlFor="supporting_docs" className="form-label">
                        Other Files
                        <CTooltip content="Accepts 1-2 file/s">
                          <span className="ms-2" style={{ marginLeft: "5px" }}>
                            <i className="bi bi-info-circle"></i>
                          </span>
                        </CTooltip>
                      </label>
                      <FieldArray name="supporting_docs">
                        {({ push, remove, form }) => (
                          <>
                            {form.values.supporting_docs.length === 0 && (
                              <div className="d-flex justify-content-start mb-2">
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={() => push("")}
                                >
                                  Add File
                                </button>
                              </div>
                            )}
                            {form.values.supporting_docs.map((file, index) => (
                              <div key={index} className="d-flex mb-2">
                                <input
                                  id={`supporting_docs.${index}`}
                                  name={`supporting_docs.${index}`}
                                  type="file"
                                  className={`form-control me-2 ${touched.supporting_docs && errors.supporting_docs ? "is-invalid" : ""}`}
                                  onChange={(event) => {
                                    const files = Array.from(event.currentTarget.files);
                                    setFieldValue(`supporting_docs.${index}`, files[0]);
                                  }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={() => remove(index)}
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            {form.values.supporting_docs.length > 0 && form.values.supporting_docs.length < 2 && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => push("")}
                              >
                                Add File
                              </button>
                            )}
                          </>
                        )}
                      </FieldArray>
                      <ErrorMessage
                        name="supporting_docs"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-12 mb-3">
                      <label htmlFor="plan_benefits" className="form-label">
                        <span className="text-danger">*</span> Plan Benefits
                      </label>
                      <Editor
                        editorState={editorState}
                        onEditorStateChange={(state) => handleEditorChange(state, setFieldValue, setFieldTouched)}
                        wrapperClassName="demo-wrapper"
                        editorClassName={`demo-editor ${touched.plan_benefits && errors.plan_benefits ? "is-invalid" : ""}`}
                        editorStyle={{ border: '1px solid #ced4da', borderRadius: '4px', padding: '5px' }}
                      />
                      {touched.plan_benefits && errors.plan_benefits && (
                        <div className="invalid-feedback d-block">{errors.plan_benefits}</div>
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={() => navigate("/orders")}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      Upload
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default UploadQuotation;
